// Billing API Service
// Handles all billing-related API calls for plan management, usage tracking, and subscriptions

import { authenticatedFetch } from './authenticatedFetch';

import { logger } from '../utils/logger';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface BillingPlanLimits {
  credits: {
    used: number;
    limit: number;
    remaining: number;
    unlimited: boolean;
  };
  members: {
    current: number;
    max: number | null; // null = unlimited
    unlimited: boolean;
  };
  projects: {
    current: number;
    max: number | null; // null = unlimited
    unlimited: boolean;
  };
}

export interface BillingPlanFeatures {
  api_access?: boolean;
  priority_support?: boolean;
  custom_branding?: boolean;
  advanced_analytics?: boolean;
  sla_guarantees?: boolean;
  best_for?: string;
  plan_key?: string;
  allows_topup?: boolean;
  billing_type?: 'monthly' | 'yearly';
  url_monitoring?: boolean;
  similarity_audits?: boolean;
  briefs_ai_articles?: boolean;
  [key: string]: boolean | string | undefined;
}

export interface BillingPlan {
  type: 'free' | 'pro' | 'team' | 'enterprise' | 'starter' | 'agency';
  name: string;
  credits_per_month?: number; // NEW: Credits allocated per month for this plan
  limits: BillingPlanLimits;
  features: BillingPlanFeatures;
}

export interface NextBillingInfo {
  next_billing_date: string;
  credits_to_be_granted: number;
  plan_name: string;
  has_pending_change: boolean;
  pending_change?: {
    pending_plan_id: string;
    pending_plan_name: string;
    pending_credits_per_month: number;
    effective_date: string;
    reason: 'downgrade' | 'upgrade';
    from_plan_name?: string;
    from_credits_per_month?: number;
  };
}

export interface BillingInfo {
  organization_id: string;
  entitlement?: {
    state: string;
    serviceable: boolean;
    reason: string | null;
    subscription_status?: string | null;
  } | null;
  plan: BillingPlan;
  billing_period: {
    start: string;
    end: string;
    days_remaining: number;
    anchor_day: number;
  };
  subscription: {
    billing_source?: 'stripe' | 'internal_testing' | 'admin_override';
    status: 'active' | 'trialing' | 'past_due' | 'unpaid' | 'canceled' | 'free';
    stripe_subscription_id: string | null;
    cancel_at_period_end: boolean;
    canceled_at: string | null;
    trial_end: string | null;
  };
  next_billing?: NextBillingInfo;
}

// Backend response format for plans (actual structure from backend)
export interface AvailablePlan {
  id: string;
  type: 'free' | 'starter' | 'pro' | 'agency' | 'team' | 'enterprise';
  name: string;
  description: string;
  price: {
    monthly: number;
    yearly: number;
    currency: 'usd' | 'eur' | 'czk';
  };
  limits: {
    credits_per_month: number | null;
    max_members?: number | null;
    max_projects?: number | null;
  };
  features: BillingPlanFeatures;
  is_current?: boolean;
  stripe_price_id?: string;
  // Computed fields for backwards compatibility
  plan_type?: 'free' | 'starter' | 'pro' | 'agency' | 'team' | 'enterprise';
}

export interface AvailablePlansResponse {
  plans: AvailablePlan[];
  currencies: string[]; // Available currencies: ["usd", "eur", "czk"]
}

export interface UsageEvent {
  id: string;
  timestamp: string;
  event_type: string;
  quantity: number;
  metadata?: {
    status?: string;
    total_after?: number;
    organization_id?: string;
    [key: string]: unknown;
  };
}

export interface UsageHistoryResponse {
  organization_id: string;
  period: {
    start: string;
    end: string;
  };
  summary: {
    total_credits_used: number;
    credits_limit: number;
    credits_remaining: number;
    by_service: Record<string, number>;
  };
  usage_events: UsageEvent[];
}

export interface UpgradePlanRequest {
  plan_type: 'pro' | 'team' | 'enterprise' | 'starter' | 'agency';
  billing_interval: 'monthly' | 'yearly';
  currency?: 'usd' | 'eur' | 'czk';
}

// Response from POST /auth/organizations/{org_id}/billing/upgrade
export interface UpgradePlanResponse {
  checkout_url: string; // Stripe checkout URL
  session_id: string; // Stripe session ID
  expires_at: string; // ISO date when session expires
  // Legacy fields for backwards compatibility
  success?: boolean;
  message?: string;
  plan_type?: string;
  plan_name?: string;
  amount?: number;
  currency?: string;
  interval?: string;
}

// Response from POST /auth/organizations/{org_id}/billing/change-plan
export interface ChangePlanResponse {
  organization_id: string;
  old_plan_id: string;
  new_plan_id: string;
  subscription_id: string;
  status: 'upgraded' | 'downgrade_scheduled'; // Updated: was 'downgraded'
  effective_date: string; // ISO date
  message: string;
  next_billing?: NextBillingInfo; // NEW: included for downgrades
}

// Response from GET /auth/organizations/{org_id}/subscription
export interface SubscriptionInfo {
  subscription_id: string | null;
  organization_id: string;
  plan: {
    id?: string;
    name: string;
    plan_type: 'free' | 'starter' | 'pro' | 'agency' | 'team' | 'enterprise';
    price: number;
    currency: string;
    interval: 'month' | 'year';
    credits_per_month: number; // NEW FIELD
  };
  status: 'active' | 'trialing' | 'past_due' | 'unpaid' | 'canceled' | 'free';
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at?: string | null;
  trial_end?: string | null;
  next_billing?: NextBillingInfo; // NEW FIELD
}

export interface CancelSubscriptionRequest {
  cancel_at_period_end: boolean;
  reason?: string;
}

// Response from POST /auth/organizations/{org_id}/billing/cancel-subscription
export interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
  cancel_at_period_end: boolean;
  current_period_end: string; // ISO date
  organization_id: string;
}

export interface BillingAddress {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

export interface BillingDetails {
  organization_id: string;
  customer_id: string;
  email?: string | null;
  billing_email?: string | null;
  company_name?: string | null;
  billing_address: BillingAddress;
  tax_id_type?: string | null;
  tax_id_value?: string | null;
  stripe_customer_id?: string | null;
  stripe_sync_status: 'unknown' | 'deferred' | 'synced' | 'failed' | string;
  stripe_sync_error?: string | null;
}

export interface BillingDetailsUpdateRequest {
  billing_email?: string | null;
  company_name?: string | null;
  billing_address?: BillingAddress;
  tax_id_type?: string | null;
  tax_id_value?: string | null;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalize billing info to ensure consistent structure
 * Handles both nested and flat response formats from backend
 */
function normalizeBillingInfo(data: any): BillingInfo {
  logger.log('🔧 Normalizing billing data:', data);

  // Backend returns the correct structure - just validate and return
  // The backend /billing endpoint returns:
  // - plan.limits.credits.{used, limit, remaining, unlimited}
  // - plan.limits.members.{current, max, unlimited}
  // - plan.limits.projects.{current, max, unlimited}
  // - billing_period.{start, end, days_remaining, anchor_day}
  // - subscription.{status, stripe_subscription_id, cancel_at_period_end, canceled_at, trial_end}

  logger.log('✅ Using backend data directly (backend is source of truth)');
  return data as BillingInfo;
}

// ============================================================================
// API Request Helper
// ============================================================================

/**
 * Wrapper for authenticatedFetch with billing-specific service name
 * This ensures all billing API calls use the centralized auth handling
 * which includes automatic token refresh on 401 errors
 */
async function billingApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  return authenticatedFetch<T>(endpoint, {
    ...options,
    serviceName: 'Billing API'
  });
}

// ============================================================================
// Billing API Methods
// ============================================================================

export const billingAPI = {
  /**
   * Get current billing information for an organization
   * @param organizationId - Organization ID
   * @returns Billing information including plan, limits, and usage
   */
  async getBillingInfo(organizationId: string): Promise<BillingInfo> {
    const data = await billingApiRequest<any>(
      `/auth/organizations/${organizationId}/billing`,
      { method: 'GET' }
    );
    return normalizeBillingInfo(data);
  },

  /**
   * Get all available plans
   * @param currency - Optional currency filter (usd, eur, czk)
   * @returns List of available plans with pricing and features
   */
  async getAvailablePlans(currency?: 'usd' | 'eur' | 'czk'): Promise<AvailablePlansResponse> {
    const endpoint = currency
      ? `/auth/billing/plans?currency=${currency}`
      : '/auth/billing/plans';
    return billingApiRequest<AvailablePlansResponse>(endpoint, { method: 'GET' });
  },

  /**
   * Get current subscription for an organization
   * @deprecated Use getBillingInfo instead - /subscription endpoint is deprecated
   * @param organizationId - Organization ID
   * @returns Subscription info including plan, status, and period dates
   */
  async getSubscription(organizationId: string): Promise<SubscriptionInfo> {
    logger.warn('⚠️ getSubscription is deprecated. Use getBillingInfo instead.');
    return billingApiRequest<SubscriptionInfo>(
      `/auth/organizations/${organizationId}/subscription`,
      { method: 'GET' }
    );
  },

  /**
   * Get usage history for an organization
   * @param organizationId - Organization ID
   * @param params - Optional filters (start_date, end_date, service)
   * @returns Usage history with events and summary
   */
  async getUsageHistory(
    organizationId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      service?: string;
    }
  ): Promise<UsageHistoryResponse> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.service) queryParams.append('service', params.service);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/auth/organizations/${organizationId}/billing/usage?${queryString}`
      : `/auth/organizations/${organizationId}/billing/usage`;

    return billingApiRequest<UsageHistoryResponse>(endpoint, { method: 'GET' });
  },

  /**
   * Upgrade organization plan
   * @param organizationId - Organization ID
   * @param planType - Target plan type
   * @param billingInterval - Billing interval (monthly or yearly)
   * @param currency - Currency (usd, eur, czk)
   * @returns Upgrade result with new limits or Stripe checkout URL
   */
  async upgradePlan(
    organizationId: string,
    planType: string,
    billingInterval: 'monthly' | 'yearly' = 'monthly',
    currency: 'usd' | 'eur' | 'czk' = 'usd'
  ): Promise<UpgradePlanResponse> {
    const requestBody: any = {
      plan_type: planType,
      billing_interval: billingInterval,
      currency: currency, // Always include currency parameter
    };

    logger.log('📤 Sending upgrade request:', {
      organizationId,
      ...requestBody,
    });

    // Store request in sessionStorage for debugging (persists through redirect)
    sessionStorage.setItem('last_upgrade_request', JSON.stringify({
      timestamp: new Date().toISOString(),
      organizationId,
      ...requestBody,
    }));

    const response = await billingApiRequest<UpgradePlanResponse>(
      `/auth/organizations/${organizationId}/billing/upgrade`,
      {
        method: 'POST',
        body: JSON.stringify(requestBody),
      }
    );

    logger.log('📥 Received upgrade response:', response);

    // Store response in sessionStorage for debugging (persists through redirect)
    sessionStorage.setItem('last_upgrade_response', JSON.stringify({
      timestamp: new Date().toISOString(),
      ...response,
    }));

    return response;
  },

  /**
   * Change plan for existing subscription (upgrade or downgrade)
   * @param organizationId - Organization ID
   * @param planType - Target plan type
   * @param billingInterval - Billing interval (monthly or yearly)
   * @param currency - Currency (usd, eur, czk)
   * @returns Change result with effective date
   */
  async changePlan(
    organizationId: string,
    planType: string,
    billingInterval: 'monthly' | 'yearly' = 'monthly',
    currency: 'usd' | 'eur' | 'czk' = 'usd'
  ): Promise<ChangePlanResponse> {
    const requestBody = {
      plan_type: planType,
      billing_interval: billingInterval,
      currency: currency,
    };

    logger.log('📤 Sending change-plan request:', {
      organizationId,
      ...requestBody,
    });

    const response = await billingApiRequest<ChangePlanResponse>(
      `/auth/organizations/${organizationId}/billing/change-plan`,
      {
        method: 'POST',
        body: JSON.stringify(requestBody),
      }
    );

    logger.log('📥 Received change-plan response:', response);
    return response;
  },

  /**
   * Cancel subscription
   * @param organizationId - Organization ID
   * @param cancelAtPeriodEnd - Whether to cancel at period end or immediately
   * @param reason - Optional cancellation reason
   * @returns Cancellation result
   */
  async cancelSubscription(
    organizationId: string,
    cancelAtPeriodEnd: boolean = true,
    reason?: string
  ): Promise<CancelSubscriptionResponse> {
    return billingApiRequest<CancelSubscriptionResponse>(
      `/auth/organizations/${organizationId}/billing/cancel-subscription`,
      {
        method: 'POST',
        body: JSON.stringify({
          cancel_at_period_end: cancelAtPeriodEnd,
          reason,
        }),
      }
    );
  },

  /**
   * Get invoice-facing billing details for an organization.
   * @param organizationId - Organization ID
   * @returns Billing details used for Stripe invoices and receipts
   */
  async getBillingDetails(organizationId: string): Promise<BillingDetails> {
    return billingApiRequest<BillingDetails>(
      `/auth/organizations/${organizationId}/billing-details`,
      { method: 'GET' }
    );
  },

  /**
   * Update invoice-facing billing details for an organization.
   * Empty optional fields should be sent as null when callers intend to clear them.
   * @param organizationId - Organization ID
   * @param payload - Billing details updates
   * @returns Updated billing details including Stripe sync status
   */
  async updateBillingDetails(
    organizationId: string,
    payload: BillingDetailsUpdateRequest
  ): Promise<BillingDetails> {
    return billingApiRequest<BillingDetails>(
      `/auth/organizations/${organizationId}/billing-details`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    );
  },

  /**
   * Verify Stripe checkout session and process upgrade
   * This endpoint tells the backend to verify the session with Stripe and process the upgrade
   * @param sessionId - Stripe checkout session ID
   * @returns Verification result
   */
  async verifyCheckoutSession(sessionId: string): Promise<{
    success: boolean;
    message: string;
    plan_name?: string;
    upgrade_processed?: boolean;
  }> {
    logger.log('🔍 Verifying checkout session:', sessionId);
    const response = await billingApiRequest<{
      success: boolean;
      message: string;
      plan_name?: string;
      upgrade_processed?: boolean;
    }>(
      `/auth/billing/success?session_id=${sessionId}`,
      { method: 'GET' }
    );
    logger.log('✅ Session verification response:', response);
    return response;
  },
};

// Export standalone functions for convenience
export const getBillingInfo = billingAPI.getBillingInfo.bind(billingAPI);
export const getAvailablePlans = billingAPI.getAvailablePlans.bind(billingAPI);
export const getSubscription = billingAPI.getSubscription.bind(billingAPI);
export const getUsageHistory = billingAPI.getUsageHistory.bind(billingAPI);
export const upgradePlan = billingAPI.upgradePlan.bind(billingAPI);
export const changePlan = billingAPI.changePlan.bind(billingAPI);
export const cancelSubscription = billingAPI.cancelSubscription.bind(billingAPI);
export const getBillingDetails = billingAPI.getBillingDetails.bind(billingAPI);
export const updateBillingDetails = billingAPI.updateBillingDetails.bind(billingAPI);
export const verifyCheckoutSession = billingAPI.verifyCheckoutSession.bind(billingAPI);

